import { useState, useEffect } from 'react';
import { Horizon, Btn, Mo, Bd, Dp } from './shared';
import { GOLD, EMBER, MONO } from '../lib/constants';

const API = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════════════ */

const EMPLOYMENT_OPTIONS = [
  { id: 'full_time', label: 'Employed Full-Time', icon: '💼' },
  { id: 'part_time', label: 'Employed Part-Time', icon: '⏰' },
  { id: 'self_employed', label: 'Self-Employed / Business Owner', icon: '🏢' },
  { id: 'freelancer', label: 'Freelancer / Gig Worker', icon: '💻' },
  { id: 'retired', label: 'Retired', icon: '🌴' },
  { id: 'pension', label: 'On Pension / Disability', icon: '🛡️' },
  { id: 'unemployed', label: 'Unemployed', icon: '🔍' },
];

const WORK_TYPE_SUGGESTIONS = [
  'Food Service', 'Photography', 'Consulting', 'E-Commerce', 
  'Trades', 'Rideshare / Delivery', 'Real Estate', 'Other'
];

const W2_INCOME_OPTIONS = [
  { id: 'direct_deposit', label: 'Direct Deposit' },
  { id: 'other', label: 'Other' },
];

const SELF_EMPLOYED_INCOME_OPTIONS = [
  { id: 'business_sales', label: 'Business Sales / Daily Revenue' },
  { id: 'pos_deposits', label: 'POS / Merchant Deposits', subtitle: 'Square, Toast, Clover, etc.' },
  { id: 'client_invoicing', label: 'Client Invoicing' },
  { id: 'gig_payouts', label: 'Gig App Payouts' },
  { id: 'cash', label: 'Cash' },
  { id: 'commission', label: 'Commission' },
  { id: 'mixed', label: 'Mixed Sources' },
];

const ACCOUNT_TYPE_OPTIONS = [
  { id: 'personal', label: 'Personal Only' },
  { id: 'business', label: 'Business Only' },
  { id: 'both', label: 'Both Personal & Business' },
];

/* Personal expense definitions */
const PERSONAL_EXPENSES = [
  { id: 'housing', label: 'Rent or Mortgage', icon: '🏠', subQuestion: 'rent_or_mortgage' },
  { id: 'utilities', label: 'Utilities', subtitle: 'Electric / Gas', icon: '⚡' },
  { id: 'water', label: 'Water', icon: '💧' },
  { id: 'internet', label: 'Internet', icon: '📶' },
  { id: 'phone', label: 'Phone Service', icon: '📱' },
  { id: 'car_payment', label: 'Car Payment', icon: '🚗' },
  { id: 'car_insurance', label: 'Car Insurance', icon: '🛡️' },
  { id: 'health_insurance', label: 'Health Insurance', subtitle: 'Out of pocket', icon: '🏥' },
  { id: 'life_insurance', label: 'Life Insurance', icon: '❤️' },
  { id: 'investments', label: 'Investments', subtitle: 'Stocks, crypto, retirement', icon: '📈', subQuestion: 'frequency' },
  { id: 'childcare', label: 'Child Care or Support', icon: '👶' },
  { id: 'other', label: 'Other Fixed Expenses', icon: '📋', isOther: true },
];

/* Business expense definitions */
const BUSINESS_EXPENSES = [
  { id: 'biz_rent', label: 'Business Rent / Lease', subtitle: 'Commercial space, workspace', icon: '🏪' },
  { id: 'biz_utilities', label: 'Business Utilities', subtitle: 'Electric/gas/water at business', icon: '⚡' },
  { id: 'biz_internet_phone', label: 'Business Internet / Phone', icon: '📞' },
  { id: 'biz_insurance', label: 'Business Insurance', subtitle: 'Liability, commercial, etc.', icon: '🛡️' },
  { id: 'inventory', label: 'Inventory / Supplies / COGS', subtitle: 'Cost of goods sold', icon: '📦', isVariable: true },
  { id: 'software', label: 'Business Software', subtitle: 'POS, accounting, subscriptions', icon: '💻' },
  { id: 'equipment', label: 'Equipment', subtitle: 'Purchases or leases', icon: '🔧', isVariable: true },
  { id: 'payroll', label: 'Employees / Contractors', subtitle: 'Payroll expenses', icon: '👥', isVariable: true },
  { id: 'vehicle', label: 'Business Vehicle / Fuel', subtitle: 'Mileage expenses', icon: '🚚' },
  { id: 'taxes', label: 'Taxes', subtitle: 'Quarterly, sales tax remittance', icon: '📊', isVariable: true },
  { id: 'biz_other', label: 'Other Business Costs', icon: '📋', isOther: true, isVariable: true },
];

/* ══════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════ */

function ProgressBar({ current, total }) {
  const pct = ((current + 1) / total) * 100;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>PROGRESS</Mo>
        <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>{current + 1} / {total}</Mo>
      </div>
      <div style={{ height: 4, background: 'var(--ghost)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: GOLD, borderRadius: 2, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
}

function OptionButton({ label, icon, selected, onClick, subtitle }) {
  return (
    <button onClick={onClick} data-testid={`option-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        width: '100%', padding: '16px 20px', marginBottom: 10,
        background: selected ? `${GOLD}15` : 'var(--surface)',
        border: `1px solid ${selected ? GOLD : 'var(--ghost)'}`,
        borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.2s ease',
      }}>
      {icon && <span style={{ fontSize: 20 }}>{icon}</span>}
      <div style={{ flex: 1, textAlign: 'left' }}>
        <Bd style={{ fontSize: 14, color: selected ? 'var(--gold)' : 'var(--light)', fontWeight: selected ? 600 : 400 }}>{label}</Bd>
        {subtitle && <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{subtitle}</Mo>}
      </div>
      {selected && (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill={GOLD} />
          <path d="M8 12l2.5 2.5L16 9" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

function YesNoButtons({ value, onChange, isVariable }) {
  if (isVariable) {
    // Three-option selector for variable expenses
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
        <button onClick={() => onChange('regular')} data-testid="yes-regular-btn"
          style={{
            padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
            background: value === 'regular' ? `${GOLD}20` : 'var(--surface)',
            border: `2px solid ${value === 'regular' ? GOLD : 'var(--ghost)'}`, transition: 'all 0.2s ease',
            textAlign: 'left',
          }}>
          <Dp style={{ fontSize: 15, fontWeight: 700, color: value === 'regular' ? 'var(--gold)' : 'var(--light)', display: 'block' }}>Yes, regularly</Dp>
          <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4 }}>I can estimate a monthly amount</Mo>
        </button>
        <button onClick={() => onChange('occasional')} data-testid="yes-occasional-btn"
          style={{
            padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
            background: value === 'occasional' ? `${GOLD}20` : 'var(--surface)',
            border: `2px solid ${value === 'occasional' ? GOLD : 'var(--ghost)'}`, transition: 'all 0.2s ease',
            textAlign: 'left',
          }}>
          <Dp style={{ fontSize: 15, fontWeight: 700, color: value === 'occasional' ? 'var(--gold)' : 'var(--light)', display: 'block' }}>Yes, occasionally / It varies</Dp>
          <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4 }}>Amount fluctuates month to month</Mo>
        </button>
        <button onClick={() => onChange(false)} data-testid="no-btn"
          style={{
            padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
            background: value === false ? 'var(--faint)' : 'var(--surface)',
            border: `2px solid ${value === false ? 'var(--mute)' : 'var(--ghost)'}`, transition: 'all 0.2s ease',
            textAlign: 'left',
          }}>
          <Dp style={{ fontSize: 15, fontWeight: 700, color: value === false ? 'var(--light)' : 'var(--mute)' }}>No</Dp>
        </button>
      </div>
    );
  }
  
  // Original two-option selector for fixed expenses
  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
      <button onClick={() => onChange(true)} data-testid="yes-btn"
        style={{
          flex: 1, padding: '18px 24px', borderRadius: 12, cursor: 'pointer',
          background: value === true ? `${GOLD}20` : 'var(--surface)',
          border: `2px solid ${value === true ? GOLD : 'var(--ghost)'}`, transition: 'all 0.2s ease',
        }}>
        <Dp style={{ fontSize: 16, fontWeight: 700, color: value === true ? 'var(--gold)' : 'var(--light)' }}>Yes</Dp>
      </button>
      <button onClick={() => onChange(false)} data-testid="no-btn"
        style={{
          flex: 1, padding: '18px 24px', borderRadius: 12, cursor: 'pointer',
          background: value === false ? 'var(--faint)' : 'var(--surface)',
          border: `2px solid ${value === false ? 'var(--mute)' : 'var(--ghost)'}`, transition: 'all 0.2s ease',
        }}>
        <Dp style={{ fontSize: 16, fontWeight: 700, color: value === false ? 'var(--light)' : 'var(--mute)' }}>No</Dp>
      </button>
    </div>
  );
}

function AmountInput({ value, onChange, placeholder = 'Enter amount' }) {
  return (
    <div style={{ position: 'relative', marginTop: 16 }}>
      <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: 'var(--mute)', fontFamily: MONO }}>$</span>
      <input type="number" inputMode="decimal" value={value || ''} onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
        placeholder={placeholder} data-testid="amount-input"
        style={{
          width: '100%', padding: '16px 20px 16px 36px', background: 'var(--surface)',
          border: '1px solid var(--ghost)', borderRadius: 12, color: 'var(--light)',
          fontSize: 18, fontFamily: MONO, outline: 'none',
        }}
      />
      <Mo style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--mute)' }}>/month</Mo>
    </div>
  );
}

function ToggleSwitch({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', background: 'var(--surface)', borderRadius: 10, padding: 4, marginTop: 16 }}>
      {options.map(opt => (
        <button key={opt.id} onClick={() => onChange(opt.id)} data-testid={`toggle-${opt.id}`}
          style={{
            flex: 1, padding: '12px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: value === opt.id ? GOLD : 'transparent', transition: 'all 0.2s ease',
          }}>
          <Mo style={{ fontSize: 12, fontWeight: 600, color: value === opt.id ? '#000' : 'var(--mute)' }}>{opt.label}</Mo>
        </button>
      ))}
    </div>
  );
}

function SuggestionChip({ label, selected, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', background: selected ? `${GOLD}20` : 'var(--faint)',
      border: `1px solid ${selected ? GOLD : 'transparent'}`, borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s ease',
    }}>
      <Mo style={{ fontSize: 12, color: selected ? 'var(--gold)' : 'var(--light)' }}>{label}</Mo>
    </button>
  );
}

/* ══════════════════════════════════════════════
   PROFILE STEP COMPONENTS (Part 1)
   ══════════════════════════════════════════════ */

function StepEmployment({ value, onChange }) {
  return (
    <div className="fade-in">
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        What's your employment situation?
      </Dp>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 28, lineHeight: 1.5 }}>
        This helps us understand your income patterns
      </Bd>
      <div>
        {EMPLOYMENT_OPTIONS.map((opt) => (
          <OptionButton key={opt.id} label={opt.label} icon={opt.icon} selected={value === opt.id} onClick={() => onChange(opt.id)} />
        ))}
      </div>
    </div>
  );
}

function StepWorkType({ value, onChange }) {
  const [customValue, setCustomValue] = useState(value || '');
  useEffect(() => { setCustomValue(value || ''); }, [value]);
  const handleSuggestionClick = (sug) => { setCustomValue(sug); onChange(sug); };
  const handleInputChange = (e) => { setCustomValue(e.target.value); onChange(e.target.value); };
  
  return (
    <div className="fade-in">
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        What type of work or business?
      </Dp>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 28, lineHeight: 1.5 }}>
        Tell us about your industry or trade
      </Bd>
      <input type="text" value={customValue} onChange={handleInputChange} placeholder="e.g., Photography, Consulting..."
        data-testid="work-type-input" style={{
          width: '100%', padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--ghost)',
          borderRadius: 12, color: 'var(--light)', fontSize: 15, fontFamily: MONO, marginBottom: 20, outline: 'none',
        }}
      />
      <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>SUGGESTIONS</Mo>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {WORK_TYPE_SUGGESTIONS.map((sug) => (
          <SuggestionChip key={sug} label={sug} selected={customValue === sug} onClick={() => handleSuggestionClick(sug)} />
        ))}
      </div>
    </div>
  );
}

function StepIncomeMethod({ value, onChange, employmentType }) {
  const isSelfEmployed = ['self_employed', 'freelancer'].includes(employmentType);
  const options = isSelfEmployed ? SELF_EMPLOYED_INCOME_OPTIONS : W2_INCOME_OPTIONS;
  
  return (
    <div className="fade-in">
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        How do you receive income?
      </Dp>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 28, lineHeight: 1.5 }}>
        {isSelfEmployed ? 'Select how you typically get paid' : 'How does your paycheck arrive?'}
      </Bd>
      <div>
        {options.map((opt) => (
          <div key={opt.id} onClick={() => onChange(opt.id)} data-testid={`income-option-${opt.id}`}
            style={{
              padding: '18px 24px', marginBottom: 12, borderRadius: 12, cursor: 'pointer',
              background: value === opt.id ? `${GOLD}10` : 'var(--surface)',
              border: `2px solid ${value === opt.id ? GOLD : 'var(--ghost)'}`, transition: 'all 0.2s ease',
            }}>
            <Bd style={{ fontSize: 15, fontWeight: 600, color: value === opt.id ? 'var(--gold)' : 'var(--light)' }}>{opt.label}</Bd>
            {opt.subtitle && (
              <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4 }}>{opt.subtitle}</Mo>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepAccountType({ value, onChange }) {
  return (
    <div className="fade-in">
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        Is this bank account...
      </Dp>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 28, lineHeight: 1.5 }}>
        Knowing this helps us categorize transactions accurately
      </Bd>
      <div>
        {ACCOUNT_TYPE_OPTIONS.map((opt) => <OptionButton key={opt.id} label={opt.label} selected={value === opt.id} onClick={() => onChange(opt.id)} />)}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   EXPENSE STEP COMPONENT (Part 2 & 3)
   ══════════════════════════════════════════════ */

function StepExpense({ expense, value, onChange, isBusiness }) {
  const hasExpense = value?.has_expense;
  const amount = value?.amount;
  const housingType = value?.housing_type;
  const frequency = value?.frequency;
  
  const otherExpenses = value?.items || [];
  const [newName, setNewName] = useState('');
  const [newAmount, setNewAmount] = useState(null);
  
  const addOtherExpense = () => {
    if (newName && newAmount) {
      const updated = [...otherExpenses, { name: newName, amount: newAmount }];
      onChange({ ...value, has_expense: 'regular', items: updated });
      setNewName(''); setNewAmount(null);
    }
  };
  
  const removeOtherExpense = (idx) => {
    const updated = otherExpenses.filter((_, i) => i !== idx);
    onChange({ ...value, items: updated, has_expense: updated.length > 0 ? 'regular' : false });
  };
  
  const showAmountField = expense.isVariable ? hasExpense === 'regular' : hasExpense === true;
  
  return (
    <div className="fade-in">
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <span style={{ fontSize: 48 }}>{expense.icon}</span>
      </div>
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        {expense.isOther 
          ? (isBusiness ? 'Any other regular business costs?' : 'Any other fixed monthly expenses?')
          : `Do you pay for ${expense.label.toLowerCase()}?`}
      </Dp>
      {expense.subtitle && (
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 8 }}>{expense.subtitle}</Bd>
      )}
      
      <YesNoButtons value={hasExpense} onChange={(v) => onChange({ ...value, has_expense: v })} isVariable={expense.isVariable} />
      
      {showAmountField && (
        <div className="fade-in" style={{ marginTop: 24 }}>
          {expense.subQuestion === 'rent_or_mortgage' && (
            <>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>TYPE</Mo>
              <ToggleSwitch options={[{ id: 'rent', label: 'Rent' }, { id: 'mortgage', label: 'Mortgage' }]} value={housingType} onChange={(v) => onChange({ ...value, housing_type: v })} />
            </>
          )}
          
          {expense.subQuestion === 'frequency' && (
            <>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>FREQUENCY</Mo>
              <ToggleSwitch options={[{ id: 'weekly', label: 'Weekly' }, { id: 'monthly', label: 'Monthly' }]} value={frequency || 'monthly'} onChange={(v) => onChange({ ...value, frequency: v })} />
            </>
          )}
          
          {expense.isOther ? (
            <>
              {otherExpenses.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {otherExpenses.map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', background: 'var(--surface)', borderRadius: 10, marginBottom: 8,
                    }}>
                      <div>
                        <Bd style={{ fontSize: 14, color: 'var(--light)' }}>{item.name}</Bd>
                        <Mo style={{ fontSize: 12, color: 'var(--gold)' }}>${item.amount}/mo</Mo>
                      </div>
                      <button onClick={() => removeOtherExpense(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>
                {otherExpenses.length > 0 ? 'ADD ANOTHER' : 'EXPENSE NAME'}
              </Mo>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={isBusiness ? "e.g., Marketing, Permits..." : "e.g., Gym membership"}
                data-testid="other-expense-name" style={{
                  width: '100%', padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--ghost)',
                  borderRadius: 10, color: 'var(--light)', fontSize: 14, fontFamily: MONO, outline: 'none', marginBottom: 12,
                }}
              />
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: 'var(--mute)', fontFamily: MONO }}>$</span>
                <input type="number" inputMode="decimal" value={newAmount || ''} onChange={(e) => setNewAmount(e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="Amount" data-testid="other-expense-amount"
                  style={{
                    width: '100%', padding: '14px 16px 14px 36px', background: 'var(--surface)', border: '1px solid var(--ghost)',
                    borderRadius: 10, color: 'var(--light)', fontSize: 16, fontFamily: MONO, outline: 'none',
                  }}
                />
              </div>
              {newName && newAmount && (
                <Btn onClick={addOtherExpense} secondary style={{ marginTop: 12 }} data-testid="add-expense-btn">+ ADD EXPENSE</Btn>
              )}
            </>
          ) : (
            <>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginTop: expense.subQuestion ? 20 : 0, marginBottom: 8 }}>
                HOW MUCH PER MONTH?
              </Mo>
              <AmountInput value={amount} onChange={(v) => onChange({ ...value, amount: v })} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   BUSINESS INTRO STEP
   ══════════════════════════════════════════════ */

function StepBusinessIntro({ onContinue }) {
  return (
    <div className="fade-in" style={{ textAlign: 'center' }}>
      <span style={{ fontSize: 64, display: 'block', marginBottom: 24 }}>💼</span>
      <Dp style={{ fontSize: 24, fontWeight: 700, color: 'var(--light)', marginBottom: 12 }}>
        Business Expenses
      </Dp>
      <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.6, marginBottom: 32 }}>
        Now let's go through your business expenses that come out of this same account.
      </Bd>
      <Btn onClick={onContinue} gold data-testid="biz-intro-continue">LET'S GO</Btn>
    </div>
  );
}

/* ══════════════════════════════════════════════
   SUMMARY SCREEN
   ══════════════════════════════════════════════ */

function SummaryScreen({ profile, personalExpenses, businessExpenses, onConfirm, onGoBack, saving }) {
  const isSelfEmployed = ['self_employed', 'freelancer'].includes(profile.employment_type);
  const hasBizExpenses = isSelfEmployed && businessExpenses.length > 0;
  
  const personalTotal = personalExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const businessTotal = businessExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  
  const ExpenseList = ({ expenses, title, total, color }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>{title}</Mo>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
        {expenses.length === 0 ? (
          <Mo style={{ fontSize: 12, color: 'var(--mute)', textAlign: 'center' }}>No expenses added</Mo>
        ) : (
          expenses.map((exp, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < expenses.length - 1 ? '1px solid var(--faint)' : 'none' }}>
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{exp.name}</Bd>
              <Mo style={{ fontSize: 13, color: 'var(--light)', fontFamily: MONO }}>${exp.amount?.toLocaleString()}</Mo>
            </div>
          ))
        )}
      </div>
      <div style={{ background: `${color}15`, border: `1px solid ${color}`, borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
        <Bd style={{ fontSize: 12, color }}>MONTHLY TOTAL</Bd>
        <Dp style={{ fontSize: 16, fontWeight: 700, color }}>${total.toLocaleString()}</Dp>
      </div>
    </div>
  );
  
  return (
    <div className="fade-in">
      <Dp style={{ fontSize: 22, fontWeight: 700, color: 'var(--light)', display: 'block', textAlign: 'center', marginBottom: 8 }}>
        Review Your Profile
      </Dp>
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', textAlign: 'center', marginBottom: 24 }}>
        Here's a summary of your fixed monthly expenses
      </Bd>
      
      {/* Profile Info */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>Employment</Mo>
          <Bd style={{ fontSize: 12, color: 'var(--light)' }}>{EMPLOYMENT_OPTIONS.find(o => o.id === profile.employment_type)?.label || '-'}</Bd>
        </div>
        {profile.work_type && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>Industry</Mo>
            <Bd style={{ fontSize: 12, color: 'var(--light)' }}>{profile.work_type}</Bd>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>Account Type</Mo>
          <Bd style={{ fontSize: 12, color: 'var(--light)' }}>{ACCOUNT_TYPE_OPTIONS.find(o => o.id === profile.account_type)?.label || 'Personal'}</Bd>
        </div>
      </div>
      
      {/* Expense Columns */}
      <div style={{ display: 'flex', gap: 16, flexDirection: hasBizExpenses ? 'row' : 'column' }}>
        <ExpenseList expenses={personalExpenses} title="PERSONAL EXPENSES" total={personalTotal} color={GOLD} />
        {hasBizExpenses && (
          <ExpenseList expenses={businessExpenses} title="BUSINESS EXPENSES" total={businessTotal} color="#22c55e" />
        )}
      </div>
      
      {/* Grand Total for Self-Employed */}
      {hasBizExpenses && (
        <div style={{ background: 'var(--faint)', borderRadius: 10, padding: '16px', marginTop: 16, textAlign: 'center' }}>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1 }}>COMBINED MONTHLY EXPENSES</Mo>
          <Dp style={{ fontSize: 24, fontWeight: 700, color: 'var(--light)', marginTop: 4 }}>${(personalTotal + businessTotal).toLocaleString()}</Dp>
        </div>
      )}
      
      {/* Actions */}
      <div style={{ marginTop: 24 }}>
        <Btn onClick={onConfirm} gold disabled={saving} data-testid="summary-confirm-btn">
          {saving ? 'SAVING...' : 'CONFIRM & CONTINUE'}
        </Btn>
        <button onClick={onGoBack} data-testid="summary-goback-btn"
          style={{ width: '100%', padding: '14px', marginTop: 12, background: 'none', border: '1px solid var(--ghost)', borderRadius: 10, cursor: 'pointer' }}>
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>GO BACK TO EDIT</Mo>
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN WIZARD COMPONENT
   ══════════════════════════════════════════════ */
export function FinancialProfileWizard({ onComplete, onSkip, initialData }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Profile data (Part 1)
  const [profile, setProfile] = useState({
    employment_type: initialData?.employment_type || null,
    work_type: initialData?.work_type || null,
    income_method: initialData?.income_method || null,
    account_type: initialData?.account_type || null,
  });
  
  // Personal expenses data (Part 2)
  const [expenses, setExpenses] = useState(() => {
    if (!initialData?.personal_expenses) return {};
    const map = {};
    for (const exp of initialData.personal_expenses) {
      if (exp.type === 'other') {
        if (!map.other) map.other = { has_expense: true, items: [] };
        map.other.items.push({ name: exp.name, amount: exp.amount });
      } else {
        map[exp.type] = { has_expense: true, amount: exp.amount, housing_type: exp.housing_type, frequency: exp.frequency };
      }
    }
    return map;
  });
  
  // Business expenses data (Part 3)
  const [bizExpenses, setBizExpenses] = useState(() => {
    if (!initialData?.business_expenses) return {};
    const map = {};
    for (const exp of initialData.business_expenses) {
      if (exp.type === 'biz_other') {
        if (!map.biz_other) map.biz_other = { has_expense: true, items: [] };
        map.biz_other.items.push({ name: exp.name, amount: exp.amount });
      } else {
        map[exp.type] = { has_expense: true, amount: exp.amount };
      }
    }
    return map;
  });

  // Update state when initialData changes (async load)
  useEffect(() => {
    if (initialData) {
      setProfile({
        employment_type: initialData.employment_type || null,
        work_type: initialData.work_type || null,
        income_method: initialData.income_method || null,
        account_type: initialData.account_type || null,
      });
      
      if (initialData.personal_expenses) {
        const map = {};
        for (const exp of initialData.personal_expenses) {
          if (exp.type === 'other') {
            if (!map.other) map.other = { has_expense: true, items: [] };
            map.other.items.push({ name: exp.name, amount: exp.amount });
          } else {
            map[exp.type] = { has_expense: true, amount: exp.amount, housing_type: exp.housing_type, frequency: exp.frequency };
          }
        }
        setExpenses(map);
      }
      
      if (initialData.business_expenses) {
        const map = {};
        for (const exp of initialData.business_expenses) {
          if (exp.type === 'biz_other') {
            if (!map.biz_other) map.biz_other = { has_expense: true, items: [] };
            map.biz_other.items.push({ name: exp.name, amount: exp.amount });
          } else {
            map[exp.type] = { has_expense: true, amount: exp.amount };
          }
        }
        setBizExpenses(map);
      }
    }
  }, [initialData]);

  // Determine which steps to show
  const isSelfEmployedOrGig = ['self_employed', 'freelancer'].includes(profile.employment_type);
  const isW2OrRetired = ['full_time', 'part_time', 'retired', 'pension'].includes(profile.employment_type);
  
  // Build dynamic step list
  const profileSteps = [
    { id: 'employment', type: 'profile' },
    ...(isSelfEmployedOrGig ? [{ id: 'work_type', type: 'profile' }] : []),
    { id: 'income_method', type: 'profile' },
    ...(isSelfEmployedOrGig ? [{ id: 'account_type', type: 'profile' }] : []),
  ];
  
  const personalExpenseSteps = PERSONAL_EXPENSES.map(exp => ({ id: `expense_${exp.id}`, type: 'expense', expense: exp }));
  
  const businessExpenseSteps = isSelfEmployedOrGig ? [
    { id: 'biz_intro', type: 'biz_intro' },
    ...BUSINESS_EXPENSES.map(exp => ({ id: `biz_expense_${exp.id}`, type: 'biz_expense', expense: exp })),
  ] : [];
  
  const summaryStep = [{ id: 'summary', type: 'summary' }];
  
  const steps = [...profileSteps, ...personalExpenseSteps, ...businessExpenseSteps, ...summaryStep];
  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;
  
  // Get current value for the step
  const getCurrentValue = () => {
    if (currentStep?.type === 'expense') return expenses[currentStep.expense.id];
    if (currentStep?.type === 'biz_expense') return bizExpenses[currentStep.expense.id];
    switch (currentStep?.id) {
      case 'employment': return profile.employment_type;
      case 'work_type': return profile.work_type;
      case 'income_method': return profile.income_method;
      case 'account_type': return profile.account_type;
      default: return null;
    }
  };

  // Handle value change
  const handleChange = (value) => {
    if (currentStep?.type === 'expense') {
      setExpenses(e => ({ ...e, [currentStep.expense.id]: value }));
      return;
    }
    if (currentStep?.type === 'biz_expense') {
      setBizExpenses(e => ({ ...e, [currentStep.expense.id]: value }));
      return;
    }
    switch (currentStep?.id) {
      case 'employment': setProfile(p => ({ ...p, employment_type: value })); break;
      case 'work_type': setProfile(p => ({ ...p, work_type: value })); break;
      case 'income_method': setProfile(p => ({ ...p, income_method: value })); break;
      case 'account_type': setProfile(p => ({ ...p, account_type: value })); break;
    }
  };

  // Check if can proceed
  const canProceed = () => {
    if (currentStep?.type === 'biz_intro' || currentStep?.type === 'summary') return true;
    if (currentStep?.type === 'expense' || currentStep?.type === 'biz_expense') {
      const val = currentStep.type === 'expense' ? expenses[currentStep.expense.id] : bizExpenses[currentStep.expense.id];
      if (val?.has_expense === undefined) return false;
      if (val?.has_expense === true) {
        if (currentStep.expense.isOther) return val.items && val.items.length > 0;
        if (currentStep.expense.subQuestion === 'rent_or_mortgage' && !val.housing_type) return false;
        return val.amount > 0;
      }
      return true;
    }
    const value = getCurrentValue();
    if (currentStep?.id === 'work_type') return value && value.trim().length > 0;
    return value !== null;
  };

  // Build expense arrays for saving
  const buildExpenseArrays = () => {
    const personalExpenses = [];
    for (const [key, val] of Object.entries(expenses)) {
      if (val?.has_expense) {
        if (key === 'other' && val.items) {
          val.items.forEach(item => personalExpenses.push({ type: 'other', name: item.name, amount: item.amount }));
        } else {
          const expDef = PERSONAL_EXPENSES.find(e => e.id === key);
          personalExpenses.push({
            type: key, name: expDef?.label || key, amount: val.amount,
            ...(val.housing_type && { housing_type: val.housing_type }),
            ...(val.frequency && { frequency: val.frequency }),
          });
        }
      }
    }
    
    const businessExpensesArr = [];
    for (const [key, val] of Object.entries(bizExpenses)) {
      if (val?.has_expense) {
        if (key === 'biz_other' && val.items) {
          val.items.forEach(item => businessExpensesArr.push({ type: 'biz_other', name: item.name, amount: item.amount }));
        } else {
          const expDef = BUSINESS_EXPENSES.find(e => e.id === key);
          businessExpensesArr.push({ type: key, name: expDef?.label || key, amount: val.amount });
        }
      }
    }
    
    return { personalExpenses, businessExpenses: businessExpensesArr };
  };

  // Handle next step or save
  const handleNext = async () => {
    if (currentStep?.type === 'summary') {
      await saveProfile();
    } else {
      if (currentStep?.id === 'income_method' && isW2OrRetired) {
        setProfile(p => ({ ...p, account_type: 'personal' }));
      }
      setStep(s => s + 1);
    }
  };

  // Save profile to backend
  const saveProfile = async () => {
    setSaving(true);
    try {
      const { personalExpenses, businessExpenses } = buildExpenseArrays();
      
      const finalProfile = {
        ...profile,
        account_type: profile.account_type || 'personal',
        personal_expenses: personalExpenses,
        business_expenses: businessExpenses,
        completed_at: new Date().toISOString(),
        wizard_version: 3,
      };

      const res = await fetch(`${API}/api/user/financial-profile`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalProfile),
      });

      if (res.ok) onComplete(finalProfile);
      else onComplete(finalProfile);
    } catch (err) {
      console.error('Error saving profile:', err);
      onComplete({ ...profile, personal_expenses: [], business_expenses: [] });
    } finally {
      setSaving(false);
    }
  };

  // Handle back
  const handleBack = () => { if (step > 0) setStep(s => s - 1); };

  // Go back from summary
  const handleGoBackFromSummary = () => { setStep(s => s - 1); };

  // Render current step content
  const renderStepContent = () => {
    if (currentStep?.type === 'summary') {
      const { personalExpenses, businessExpenses } = buildExpenseArrays();
      return <SummaryScreen profile={profile} personalExpenses={personalExpenses} businessExpenses={businessExpenses}
        onConfirm={handleNext} onGoBack={handleGoBackFromSummary} saving={saving} />;
    }
    if (currentStep?.type === 'biz_intro') {
      return <StepBusinessIntro onContinue={() => setStep(s => s + 1)} />;
    }
    if (currentStep?.type === 'expense') {
      return <StepExpense expense={currentStep.expense} value={expenses[currentStep.expense.id] || {}} onChange={handleChange} isBusiness={false} />;
    }
    if (currentStep?.type === 'biz_expense') {
      return <StepExpense expense={currentStep.expense} value={bizExpenses[currentStep.expense.id] || {}} onChange={handleChange} isBusiness={true} />;
    }
    switch (currentStep?.id) {
      case 'employment': return <StepEmployment value={profile.employment_type} onChange={handleChange} />;
      case 'work_type': return <StepWorkType value={profile.work_type} onChange={handleChange} />;
      case 'income_method': return <StepIncomeMethod value={profile.income_method} onChange={handleChange} employmentType={profile.employment_type} />;
      case 'account_type': return <StepAccountType value={profile.account_type} onChange={handleChange} />;
      default: return null;
    }
  };

  // Section label
  const getSectionLabel = () => {
    if (currentStep?.type === 'biz_intro' || currentStep?.type === 'biz_expense') return 'BUSINESS EXPENSES';
    if (currentStep?.type === 'expense') return 'PERSONAL EXPENSES';
    if (currentStep?.type === 'summary') return 'SUMMARY';
    return 'FINANCIAL PROFILE';
  };

  const showProgressBar = currentStep?.type !== 'summary' && currentStep?.type !== 'biz_intro';
  const showContinueBtn = currentStep?.type !== 'summary' && currentStep?.type !== 'biz_intro';

  return (
    <div data-testid="financial-profile-wizard" className="dettio-screen">
      <Horizon intensity={0.3} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          {step > 0 && currentStep?.type !== 'summary' ? (
            <button onClick={handleBack} data-testid="wizard-back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
          ) : <div style={{ width: 36 }} />}
          
          <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 2 }}>{getSectionLabel()}</Mo>
          
          {currentStep?.type !== 'summary' ? (
            <button onClick={handleNext} data-testid="wizard-skip-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
              <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>SKIP</Mo>
            </button>
          ) : <div style={{ width: 36 }} />}
        </div>

        {/* Progress Bar */}
        {showProgressBar && <ProgressBar current={step} total={steps.length} />}

        {/* Step Content */}
        <div style={{ minHeight: currentStep?.type === 'summary' ? 'auto' : 400 }}>
          {renderStepContent()}
        </div>

        {/* Continue Button */}
        {showContinueBtn && (
          <Btn onClick={handleNext} disabled={!canProceed() || saving} gold data-testid="wizard-continue-btn"
            style={{ marginTop: 24, opacity: canProceed() ? 1 : 0.5 }}>
            {saving ? 'SAVING...' : 'CONTINUE'}
          </Btn>
        )}
      </div>
    </div>
  );
}
